import React from "react";
import { useHistory } from "react-router";
import * as moment from "moment-timezone";

import {
  THead,
  TrHead,
  TBody,
  Th,
  Tr,
  Td,
  InnerCell,
} from "@teamrota/rota-design";

import { formatDateMonthTimeShort } from "~/src/utils/formatting";
import { COLORS } from "~/src/styles/config.style";
import Icon from "~/src/components/icon";
import { EmptyText, IconDiv } from "~/src/components/payroll/style";
import IconLink from "~/src/containers/payroll/components/IconLink";

import { cleanAddress, getEmailStatus } from "../../utils";
import { ScrollableTable } from "../styles";
import { COL_WIDTHS } from ".";

import EmailInfoModal from "./modal";
import { Carousel } from "../../components/carousel";
import { ReasonPanel, StreamPanel } from "../../components/stream";

interface TableProps {
  emails: any[];
  loading: boolean;
}

const Table: React.FC<TableProps> = ({ emails, loading }) => {
  const [selectedEmailId, setSelectedEmailId] = React.useState<number | null>(
    null,
  );

  return (
    <>
      <ScrollableTable colWidths={COL_WIDTHS}>
        <THead>
          <TrHead>
            <Th>ID</Th>
            <Th>To/From</Th>
            <Th>Received</Th>
            <Th>Status</Th>
            <Th>Streams</Th>
            <Th>&nbsp;</Th>
          </TrHead>
        </THead>

        <TBody className="emails-table-scroll-body">
          {!loading && emails?.length ? (
            emails.map((email: any) => (
              <Tr key={email.id}>
                <Td>
                  <InnerCell>
                    <span>#{email.id}</span>
                  </InnerCell>
                </Td>

                <Td>
                  <InnerCell style={{ fontSize: "80%" }}>
                    <div>{cleanAddress(email.sender)}</div>
                    <div>➡️ {cleanAddress(email.mailboxAddress)}</div>
                  </InnerCell>
                </Td>

                <Td>
                  <InnerCell>
                    {email.receivedAt
                      ? formatDateMonthTimeShort(email.receivedAt)
                      : "—"}

                    {email?.streams?.length > 0 && (
                      <div style={{ fontSize: "75%" }}>
                        {moment(email.expiresAt).isBefore() ||
                        email.isExpiredManually ? (
                          <span style={{ color: COLORS.RED }}>
                            Expired{" "}
                            {email.isExpiredManually
                              ? "Manually"
                              : formatDateMonthTimeShort(email.expiresAt)}
                          </span>
                        ) : (
                          <span>
                            Expires {formatDateMonthTimeShort(email.expiresAt)}
                          </span>
                        )}
                      </div>
                    )}
                  </InnerCell>
                </Td>

                <Td>
                  <InnerCell>{getEmailStatus(email)}</InnerCell>
                </Td>

                <Td>
                  <Streams email={email} />
                </Td>

                <Td>
                  <IconDiv>
                    <IconLink
                      name="MORE"
                      size={25}
                      isButton
                      color={Icon.colors.MAIN_ORANGE}
                      tooltip="Actions"
                      onClick={() => setSelectedEmailId(email.id)}
                    />
                  </IconDiv>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={7}>
                <EmptyText>
                  {!loading && "None found 👀"}
                  {loading && "Loading... ⏳"}
                </EmptyText>
              </Td>
            </Tr>
          )}
        </TBody>
      </ScrollableTable>

      <EmailInfoModal
        emailId={selectedEmailId}
        onClose={() => setSelectedEmailId(null)}
      />
    </>
  );
};

const Streams: React.FC<{ email: any }> = ({ email }) => {
  const history = useHistory();

  const handleClickStreams = (email: any) => {
    history.push(`/shiftstream/streams/email/${email.id}`);
  };

  const panels = [];

  if (email.streams) {
    for (const stream of email.streams) {
      panels.push(
        <StreamPanel
          key={stream.id}
          stream={stream}
          onClick={() => handleClickStreams(email)}
          showMenu={false}
        />,
      );
    }
  }

  if (email.scrapeReasons) {
    for (const reason of email.scrapeReasons) {
      panels.push(
        <ReasonPanel key={reason} reason={reason} showMenu={false} />,
      );
    }
  }

  if (!panels.length) {
    return null;
  }

  return (
    <div style={{ fontSize: "80%" }}>
      <Carousel panels={panels} />
    </div>
  );
};

export default Table;
